<template>
  <component :is="layoutComponent" v-bind="layoutProps">
    <!-- Breadcrumb for iframe mode -->
    <div v-if="isEmbedMode" class="breadcrumb-strip">
      <span class="breadcrumb-item clickable" @click="goToScans">All Scans</span>
      <span class="breadcrumb-separator">›</span>
      <span class="breadcrumb-item current">View Grid</span>
    </div>

    <!-- Delete Confirmation Dialog -->
    <delete-confirm-dialog
      v-model="showDeleteDialog"
      :item-count="1"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />

    <!-- Share Modal -->
    <share-modal
      v-if="showShareModal"
      :visible="showShareModal"
      :scan-id="shareScanId"
      :scan-type="shareScanType"
      @close="closeShareModal"
      @copied="onShareCopied"
    />
    <div class="view-grid-container" :class="{ 'with-breadcrumb': isEmbedMode }">
      <!-- Full Page Map -->
      <div ref="mapContainer" class="google-map-full"></div>
      
      <!-- Loading Indicator -->
      <div v-if="scanStatus === 'pending' || scanStatus === 'loading'" class="scan-status-overlay">
        <div class="status-card">
          <div class="loading-spinner">
            <svg class="spinner" width="40" height="40" viewBox="0 0 40 40">
              <circle class="path" cx="20" cy="20" r="16" fill="none" stroke-width="3"></circle>
            </svg>
          </div>
          <h3>{{ scanStatus === 'loading' ? 'Loading quick scan data' : 'Scan in Progress' }}</h3>
          <p>{{ scanStatus === 'loading' ? 'Please wait while we load your scan results.' : 'Your scan is being processed. The map will update automatically.' }}</p>
          <div v-if="scanStatus === 'pending'" class="progress-info">
            <span>{{ completedCount }} / {{ totalCount }} locations completed</span>
          </div>
        </div>
      </div>
      
      <!-- Back to My Business Button - Floating on map -->
      <button v-if="viewingBusiness && scanStatus === 'completed'" @click="switchBackToMainBusiness" class="back-to-business-btn">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.0002 15.1663H6.00016C2.38016 15.1663 0.833496 13.6197 0.833496 9.99967V5.99967C0.833496 2.37967 2.38016 0.833008 6.00016 0.833008H10.0002C13.6202 0.833008 15.1668 2.37967 15.1668 5.99967V9.99967C15.1668 13.6197 13.6202 15.1663 10.0002 15.1663ZM6.00016 1.83301C2.92683 1.83301 1.8335 2.92634 1.8335 5.99967V9.99967C1.8335 13.073 2.92683 14.1663 6.00016 14.1663H10.0002C13.0735 14.1663 14.1668 13.073 14.1668 9.99967V5.99967C14.1668 2.92634 13.0735 1.83301 10.0002 1.83301H6.00016Z" fill="#2477F6"/>
          <path d="M9.27993 10.7531H5.99993C5.7266 10.7531 5.49993 10.5265 5.49993 10.2531C5.49993 9.97982 5.7266 9.75315 5.99993 9.75315H9.27993C10.1333 9.75315 10.8333 9.05982 10.8333 8.19982C10.8333 7.33982 10.1399 6.64648 9.27993 6.64648H4.7666C4.49327 6.64648 4.2666 6.41982 4.2666 6.14648C4.2666 5.87315 4.49327 5.64648 4.7666 5.64648H9.27993C10.6866 5.64648 11.8333 6.79315 11.8333 8.19982C11.8333 9.60649 10.6866 10.7531 9.27993 10.7531Z" fill="#2477F6"/>
          <path d="M5.71346 7.67956C5.58679 7.67956 5.46012 7.63289 5.36012 7.53289L4.31346 6.48623C4.12012 6.29289 4.12012 5.97289 4.31346 5.77956L5.36012 4.73289C5.55346 4.53956 5.87345 4.53956 6.06679 4.73289C6.26012 4.92622 6.26012 5.24623 6.06679 5.43956L5.37345 6.13289L6.06679 6.82622C6.26012 7.01956 6.26012 7.33956 6.06679 7.53289C5.97345 7.62623 5.84679 7.67956 5.71346 7.67956Z" fill="#2477F6"/>
        </svg>

        Back to My Business
      </button>

      <!-- Left Sidebar Overlay - Only show when scan is completed and not embedded -->
      <div v-if="scanStatus === 'completed' && !isEmbeddedView" class="sidebar-overlay">
        <!-- Business Card Block -->
        <div class="business-card">
          <!-- Business Info Section with Average -->
          <div class="business-section">
            <!-- Desktop view - normal layout -->
            <div class="business-info desktop-only">
              <h2 class="business-name">{{ businessInfo.business_name || 'Loading...' }}</h2>
              <div class="info-row">
                <i class="fas fa-map-marker-alt"></i>
                <span>{{ formatAddress(businessInfo) }}</span>
              </div>
              
              <div class="info-row">
                <i class="fas fa-calendar-alt"></i>
                <span>{{ formatDate(businessInfo.createdAt) || 'Loading...' }}</span>
              </div>
              
              <div class="info-row">
                <i class="fas fa-th"></i>
                <span>{{ formatGridInfo() }}</span>
              </div>
            </div>

            <!-- Mobile view - with toggle -->
            <div class="mobile-only">
              <!-- Back to My Business Button - Inside mobile card -->
              <button v-if="viewingBusiness" @click="switchBackToMainBusiness" class="mobile-back-button">
                <i class="fas fa-arrow-left"></i>
                <span>Back to My Business</span>
              </button>

              <div class="business-header-with-toggle">
                <h2 class="business-name">{{ businessInfo.business_name || 'Loading...' }}</h2>
                <div class="mobile-actions">
                  <!-- Mobile Toggle Arrow -->
                  <button class="mobile-toggle-btn" @click="toggleMobileBusinessDetails">
                    <i :class="['fas', isBusinessDetailsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                  </button>
                  <!-- Three dots menu for mobile -->
                  <button class="mobile-menu-dots"
                          @click="toggleMobileMenu"
                          :disabled="scanStatus === 'pending' || scanStatus === 'loading'">
                    <i class="fas fa-ellipsis-v"></i>
                  </button>

                  <!-- Dropdown Menu for Mobile - Same as View Report -->
                  <div v-if="showReportMenu && scanStatus === 'completed'" class="mobile-report-menu" v-click-outside="() => showReportMenu = false">
                    <div class="menu-item" @click="viewReport">
                      <i class="fas fa-chart-bar"></i>
                      <span>View Report</span>
                    </div>
                    <div class="menu-item" @click="rescanMenu">
                      <i class="mdi mdi-refresh"></i>
                      <span>Rescan</span>
                    </div>
                    <div class="menu-item" @click="shareScan">
                      <i class="mdi mdi-share-variant-outline"></i>
                      <span>Share</span>
                    </div>
                    <div class="menu-item" @click="exportPNG">
                      <i class="mdi mdi-file-image-outline"></i>
                      <span>Export as PNG</span>
                    </div>
                    <div class="menu-item" @click="exportCSV">
                      <i class="mdi mdi-file-delimited-outline"></i>
                      <span>Export as CSV</span>
                    </div>
                    <div class="menu-item delete" @click="deleteScan">
                      <i class="mdi mdi-delete-outline"></i>
                      <span>Delete</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Collapsible business details for mobile -->
              <div v-show="!isBusinessDetailsCollapsed" class="mobile-business-content">
                <div class="business-info">
                  <div class="info-row">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ formatAddress(businessInfo) }}</span>
                  </div>

                  <div class="info-row">
                    <i class="fas fa-calendar-alt"></i>
                    <span>{{ formatDate(businessInfo.createdAt) || 'Loading...' }}</span>
                  </div>

                  <div class="info-row">
                    <i class="fas fa-th"></i>
                    <span>{{ formatGridInfo() }}</span>
                  </div>
                </div>

                <!-- Average Section for Mobile - Below grid info -->
                <div class="mobile-average-section">
                  <div class="section-label">AVERAGE</div>
                  <div class="average-value">{{ avgRank }}</div>
                </div>

                <!-- Mobile Keyword Section -->
                <div class="mobile-keyword-section">
                  <div class="section-label">SEARCH KEYWORD</div>
                  <div class="keyword-value">{{ formatKeywords(businessInfo.keywords) }}</div>
                </div>

                <!-- Mobile Chart Section -->
                <div class="mobile-chart-section">
                  <div class="chart-container">
                    <canvas ref="mobileChartCanvas" width="60" height="60"></canvas>
                  </div>
                  <div class="stats-row">
                    <div class="stat-item">
                      <div class="stat-value" style="color: #3EBB80;">{{ highCount }}%</div>
                      <div class="stat-label">High</div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-value" style="color: #f59e0b;">{{ mediumCount }}%</div>
                      <div class="stat-label">Medium</div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-value" style="color: #FF0000;">{{ lowCount }}%</div>
                      <div class="stat-label">Low</div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-value" style="color: #dcdbdb;">{{ outCount }}%</div>
                      <div class="stat-label">Out</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Average Section for Desktop only -->
            <div class="average-section desktop-only">
              <div class="section-label">AVERAGE</div>
              <div class="average-value">{{ avgRank }}</div>
            </div>
          </div>
          
          <!-- Keyword Section - Hidden on mobile when collapsed -->
          <div class="keyword-section" v-show="$vuetify.breakpoint.smAndUp || !isBusinessDetailsCollapsed">
            <div class="section-label">SEARCH KEYWORD</div>
            <div class="keyword-value">{{ formatKeywords(businessInfo.keywords) }}</div>
          </div>

          <!-- Chart Section - Hidden on mobile when collapsed -->
          <div class="chart-section-inner" v-show="$vuetify.breakpoint.smAndUp || !isBusinessDetailsCollapsed">
            <div class="chart-container">
              <canvas ref="chartCanvas"></canvas>
            </div>
            <div class="stats-row">
              <div class="stat-item">
                <div class="stat-value" style="color: #3EBB80;">{{ highCount }}%</div>
                <div class="stat-label">High</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" style="color: #f59e0b;">{{ mediumCount }}%</div>
                <div class="stat-label">Medium</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" style="color: #FF0000;">{{ lowCount }}%</div>
                <div class="stat-label">Low</div>
              </div>
              <div class="stat-item">
                <div class="stat-value" style="color: #dcdbdb;">{{ outCount }}%</div>
                <div class="stat-label">Out</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Competitors Section - Bottom Aligned with Sidebar -->
        <div v-if="!isEmbeddedView" class="competitors-section" :class="{ 'expanded': showCompetitors, 'public-view': isPublicRoute }">
          <div class="competitors-header" @click="toggleCompetitors">
            <span>Competitors ({{ uniqueCompetitors.length }})</span>
            <i class="fas" :class="showCompetitors ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>

          <div v-if="showCompetitors" class="competitors-list" :style="{ maxHeight: competitorsMaxHeight }">
            <div v-for="(competitor, index) in uniqueCompetitors"
                 :key="index"
                 class="competitor-card"
                 :class="{ 'active-business': viewingBusiness && viewingBusiness.place_id === competitor.place_id }"
                 @click="switchToCompetitor(competitor)"
                 @mouseenter="showCompetitorMarker(competitor)"
                 @mouseleave="hideCompetitorMarker">
              <div class="competitor-header-row">
                <div class="competitor-title">
                  {{ competitor.name }}
                  <span v-if="competitor.isMainBusiness" class="you-label">(You)</span>
                </div>
                <div class="competitor-rank" :class="getRankBadgeClass(competitor.avgRank)">
                  Avg. rank {{ competitor.avgRank }}
                </div>
              </div>
              <div class="competitor-address">{{ formatCompetitorAddress(competitor) }}</div>
              <div class="competitor-rating">
                <span class="stars">
                  <i v-for="n in 5" :key="n" class="fas fa-star" :class="{ 'filled': n <= Math.floor(competitor.rating || 0) }"></i>
                </span>
                <span class="rating-value">{{ competitor.rating ? competitor.rating.toFixed(1) : '0.0' }}</span>
                <span class="review-count">({{ competitor.reviews || 0 }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Overlay -->
      <div v-if="showMarkerPopup" class="popup-overlay" @click="closeMarkerPopup"></div>
      
      <!-- Marker Popup -->
      <div v-if="showMarkerPopup" class="marker-popup">
        <!-- Mobile drag handle -->
        <div class="mobile-drag-handle">
          <span></span>
        </div>
        <div class="popup-header">
          <h3>Search results for this location</h3>
          <button @click="closeMarkerPopup" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="popup-coordinates">
          Coordinates: {{ selectedMarkerData && selectedMarkerData.coordinates || '' }}
        </div>
        <div class="popup-businesses">
          <div v-for="(business, index) in (selectedMarkerData && selectedMarkerData.businesses) || []" :key="index" class="business-item">
            <div class="business-rank-badge" :class="getRankBadgeColor(business.localRank || index + 1)">
              {{ business.localRank || index + 1 }}
            </div>
            <div class="business-details">
              <div class="business-header">
                <h4 class="business-name">
                  {{ business.name }}
                  <span v-if="isMainBusiness(business)" class="you-badge">You</span>
                </h4>
                <div class="rank-display" :class="getRankBadgeColor(business.avgRank ? parseFloat(business.avgRank) : (business.localRank || index + 1))">
                  Avg. rank {{ business.avgRank || (business.localRank || index + 1) }}
                </div>
              </div>
              <div class="business-address">{{ business.formatted_address || business.vicinity }}</div>
              <div class="business-meta">
                <div class="rating-section">
                  <span class="rating">{{ business.rating || 0 }}</span>
                  <span class="stars">
                    <i v-for="n in 5" :key="n" class="fas fa-star" :class="{ 'filled': n <= Math.floor(business.rating || 0) }"></i>
                  </span>
                  <span class="reviews">({{ business.user_ratings_total || 0 }})</span>
                </div>
                <!-- <button class="scan-btn">SCAN</button> -->
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Report Button Container -->
      <div v-if="!isPublicRoute" class="report-button-container">
        <button class="view-report-btn"
                @click="viewReport"
                :disabled="scanStatus === 'pending' || scanStatus === 'loading'">
          <i class="fas fa-chart-bar" v-if="scanStatus === 'completed'"></i>
          <i class="fas fa-spinner fa-spin" v-else></i>
          {{ scanStatus === 'completed' ? 'VIEW REPORT' : 'PROCESSING' }}
        </button>
        <button class="menu-dots-btn"
                @click="toggleMobileMenu"
                :disabled="scanStatus === 'pending' || scanStatus === 'loading'">
          <i class="fas fa-ellipsis-v"></i>
        </button>

        <!-- Dropdown Menu -->
        <div v-if="showReportMenu && scanStatus === 'completed'" class="report-dropdown-menu" v-click-outside="() => showReportMenu = false">
          <div class="menu-item" @click="rescanMenu">
            <i class="mdi mdi-refresh"></i>
            <span>Rescan</span>
          </div>
          <div class="menu-item" @click="shareScan">
            <i class="mdi mdi-share-variant-outline"></i>
            <span>Share</span>
          </div>
          <div class="menu-item" @click="exportPNG">
            <i class="mdi mdi-file-image-outline"></i>
            <span>Export as PNG</span>
          </div>
          <div class="menu-item" @click="exportCSV">
            <i class="mdi mdi-file-delimited-outline"></i>
            <span>Export as CSV</span>
          </div>
          <div class="menu-item delete" @click="deleteScan">
            <i class="mdi mdi-delete-outline"></i>
            <span>Delete</span>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import axios from 'axios'
import Chart from 'chart.js'
import PanelNavigation from '@/components/PanelNavigation.vue'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog.vue'
import ShareModal from '@/components/ShareModal.vue'
import embedMode from '@/mixins/embedMode'

export default {
  mixins: [embedMode],
  components: {
    PanelNavigation,
    DeleteConfirmDialog,
    ShareModal
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  },
  name: 'ViewGrid',
  metaInfo() {
    return {
      title: "View Grid | " + this.$appName
    }
  },
  computed: {
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    },
    isPublicRoute() {
      // Check if the route ends with /share or /embedded
      const path = this.$route.path;
      return path.endsWith('/share') || path.endsWith('/embedded');
    },
    isEmbeddedView() {
      // Check specifically for embedded mode
      return this.$route.params.mode === 'embedded';
    },
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    },
    embedToken() {
      return this.$store.getters['embed/embedToken'];
    },
    layoutComponent() {
      // Use div for public routes or embed mode, panel-navigation for authenticated routes
      return (this.isPublicRoute || this.isEmbedMode) ? 'div' : 'panel-navigation';
    },
    layoutProps() {
      // Add embed-container class for embed mode
      if (this.isEmbedMode) {
        return { class: 'embed-container' };
      }
      // Pass props for panel-navigation
      return (this.isPublicRoute) ? {} : { 'default-mini': true, 'mobileEnabled': true };
    },
    completedCount() {
      return this.processItems.filter(item => item.status === 'completed').length;
    },
    totalCount() {
      return this.processItems.length;
    },
    competitorsMaxHeight() {
      // In embed mode, allow list to expand more
      if (this.isEmbedMode) {
        return 'calc(100vh - 380px)';
      }
      // Default calculation for regular mode
      return 'calc(100vh - 420px)';
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      gridRectangle: null,
      gridCircle: null,
      businessInfo: {},
      processItems: [],
      avgRank: '3.00',
      highCount: 0,
      mediumCount: 0,
      lowCount: 0,
      outCount: 0,
      uniqueCompetitors: [],
      chart: null,
      showCompetitors: false,
      selectedMarkerData: null,
      showMarkerPopup: false,
      pollingInterval: null,
      scanStatus: 'loading', // Initially loading
      googleMapsApiKey: null,
      hoverMarker: null,
      viewingBusiness: null, // Track which business we're viewing (null = main business)
      originalBusinessInfo: null, // Store original business info to switch back
      // Menu and modal states
      showReportMenu: false,
      showDeleteDialog: false,
      deleting: false,
      deleteIds: [],
      showShareModal: false,
      shareScanId: null,
      shareScanType: 'quick_scan',
      // Mobile toggle state - collapsed by default on mobile
      isBusinessDetailsCollapsed: true
    }
  },
  async mounted() {
    // Track embed usage if in embedded mode
    if (this.isEmbedMode) {
      this.trackEmbedUsage('view-grid');
    }

    await this.fetchScanData();

    // For non-public routes, ensure API key is loaded from store
    if (!this.isPublicRoute && !this.$store.state.apiKeys.googleMaps.apiKey) {
      await this.$store.dispatch('apiKeys/fetchGoogleMapsKey');
    }

    // Ensure DOM is ready before loading maps
    this.$nextTick(() => {
      this.loadGoogleMaps();

      // Also create chart if scan is completed
      // On mobile, only create chart if business details are expanded
      const isMobile = this.$vuetify.breakpoint.smAndDown;
      if (this.scanStatus === 'completed' && (!isMobile || !this.isBusinessDetailsCollapsed)) {
        this.createChart();
      }
    });

    // Start polling if scan is pending
    this.startPollingIfNeeded();
  },
  methods: {
    goToScans() {
      this.$router.push('/scans');
    },
    toggleMobileMenu() {
      // Only toggle menu if scan is completed
      if (this.scanStatus === 'completed') {
        this.showReportMenu = !this.showReportMenu;
      } else {
        // Ensure menu is closed if scan is not completed
        this.showReportMenu = false;
      }
    },
    addHomeControl(center) {
      // Create the home control button
      const homeControlDiv = document.createElement('div');

      // Set CSS for the control border
      const controlUI = document.createElement('div');
      controlUI.style.backgroundColor = '#fff';
      controlUI.style.border = '2px solid #fff';
      controlUI.style.borderRadius = '3px';
      controlUI.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)';
      controlUI.style.cursor = 'pointer';
      controlUI.style.marginRight = '10px';
      controlUI.style.marginTop = '10px';
      controlUI.style.textAlign = 'center';
      controlUI.title = 'Click to recenter the map';
      homeControlDiv.appendChild(controlUI);

      // Set CSS for the control interior
      const controlText = document.createElement('div');
      controlText.style.color = 'rgb(25,25,25)';
      controlText.style.fontFamily = 'Roboto,Arial,sans-serif';
      controlText.style.fontSize = '16px';
      controlText.style.lineHeight = '38px';
      controlText.style.paddingLeft = '12px';
      controlText.style.paddingRight = '12px';
      controlText.innerHTML = '⌂';
      controlUI.appendChild(controlText);

      // Setup the click event listeners
      controlUI.addEventListener('click', () => {
        this.map.setCenter(center);
        this.map.setZoom(14);
      });

      homeControlDiv.index = 1;
      this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(homeControlDiv);
    },

    formatDate(dateString) {
      if (!dateString) return 'Loading...';
      const date = new Date(dateString);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    },

    formatAddress(businessInfo) {
      if (!businessInfo) return 'Loading...';

      // If we have a full address, use it
      if (businessInfo.address) {
        return businessInfo.address;
      }

      // Build address from components
      const parts = [];
      if (businessInfo.city) parts.push(businessInfo.city);
      if (businessInfo.state) parts.push(businessInfo.state);
      if (businessInfo.postal_code) parts.push(businessInfo.postal_code);
      if (businessInfo.country) parts.push(businessInfo.country);

      // If we have any parts, join them
      if (parts.length > 0) {
        return parts.join(', ');
      }

      // Default fallback
      return businessInfo.country || 'Service Area Business';
    },

    formatCompetitorAddress(competitor) {
      if (!competitor) return '';

      // If we have a full address, use it
      if (competitor.address) {
        return competitor.address;
      }

      // Try to get from business_data
      if (competitor.business_data) {
        const data = typeof competitor.business_data === 'string'
          ? JSON.parse(competitor.business_data)
          : competitor.business_data;

        if (data.address) return data.address;

        // Build from components
        const parts = [];
        if (data.city) parts.push(data.city);
        if (data.state) parts.push(data.state);
        if (data.postal_code) parts.push(data.postal_code);
        if (data.country) parts.push(data.country);

        if (parts.length > 0) {
          return parts.join(', ');
        }

        return data.country || 'Service Area Business';
      }

      return 'Service Area Business';
    },

    toggleCompetitors() {
      this.showCompetitors = !this.showCompetitors;
    },

    formatKeywords(keywords) {
    

      if (!keywords) return 'No keywords';

      // If it's a single string, return it directly
      if (typeof keywords === 'string') {
        // Check if it's a JSON string that needs parsing
        if (keywords.startsWith('[') || keywords.startsWith('{')) {
          try {
            const parsed = JSON.parse(keywords);
        

            // If parsed to an array
            if (Array.isArray(parsed)) {
              // For array of strings
              if (parsed.length > 0 && typeof parsed[0] === 'string') {
                return parsed.join(', ');
              }
              // For array of objects
              if (parsed.length > 0 && typeof parsed[0] === 'object') {
                // Try different property names that might contain the keyword
                return parsed.map(k => {
                  if (typeof k === 'string') return k;
                  return k.name || k.keyword || k.value || k.text || JSON.stringify(k);
                }).join(', ');
              }
              return parsed.join(', ');
            }

            // If parsed to an object
            if (typeof parsed === 'object' && !Array.isArray(parsed)) {
              return parsed.name || parsed.keyword || parsed.value || parsed.text || JSON.stringify(parsed);
            }

            return String(parsed);
          } catch (e) {
            
            // If parsing fails, return the string as is
            return keywords;
          }
        }
        // Plain string, return as is
        return keywords;
      }

      // If it's an array
      if (Array.isArray(keywords)) {
        

        // For empty array
        if (keywords.length === 0) return 'No keywords';

        // For array of strings
        if (typeof keywords[0] === 'string') {
          return keywords.join(', ');
        }

        // For array of objects
        if (typeof keywords[0] === 'object') {
          return keywords.map(k => {
            if (typeof k === 'string') return k;
            // Try different property names
            const value = k.name || k.keyword || k.value || k.text;
            if (value) return value;

            // If no known property, check what properties exist
           
            return JSON.stringify(k);
          }).join(', ');
        }
      }

      // For any other type, convert to string
     
      return String(keywords);
    },

    formatGridInfo() {
      const gridSize = this.businessInfo.grid_size || '3x3';
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3);
      const totalPins = rows * cols;
      const radius = this.businessInfo.radius || this.businessInfo.scan_radius || '1';
      const radiusUnit = this.businessInfo.radius_unit || 'mi';
      return `${totalPins} pins (${rows} × ${cols}), ${radius} ${radiusUnit} distance between pins`;
    },

    loadGoogleMaps() {
      if (window.google && window.google.maps) {
        this.initMap();
        return;
      }

      const script = document.createElement('script');
      // Use API key from backend response, then store, then env
      const apiKey = this.googleMapsApiKey ||
                    this.$store.getters['apiKeys/googleMapsKey'] ||
                    process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        console.error('No Google Maps API key configured');
        return;
      }
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initGoogleMap`;
      script.async = true;
      script.defer = true;
      
      window.initGoogleMap = () => {
        this.initMap();
      };
      
      document.head.appendChild(script);
    },

    initMap() {
      // Initialize retry counter if not exists
      if (!this.mapInitRetries) {
        this.mapInitRetries = 0;
      }

      // Check if mapContainer ref exists
      if (!this.$refs.mapContainer) {
        this.mapInitRetries++;

        // Stop retrying after 10 attempts (1 second total)
        if (this.mapInitRetries > 10) {
          console.error('Map container not found after 10 retries. Stopping.');
          return;
        }

        
        // Retry after DOM updates
        this.$nextTick(() => {
          if (this.$refs.mapContainer) {
            this.mapInitRetries = 0; // Reset counter on success
            this.initMap();
          } else {
            setTimeout(() => this.initMap(), 100);
          }
        });
        return;
      }

      // Reset retry counter on successful initialization
      this.mapInitRetries = 0;

      const mapCenter = this.getGridCenter();

      this.map = new google.maps.Map(this.$refs.mapContainer, {
        zoom: 14,
        center: mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER
        },
        mapTypeControl: false, // Disabled map type control
        scaleControl: true,
        streetViewControl: false, // Disabled street view control (Pegman)
        rotateControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Add custom home/center control
      this.addHomeControl(mapCenter);

      if (this.processItems.length > 0) {
        this.createMarkers();
        this.drawGrid();
        this.fitMapToGrid();
      }
    },

    getGridCenter() {
      if (this.processItems.length === 0) {
        return { lat: 28.5887354, lng: 77.0829215 };
      }
      
      // For circular grids, use the actual center point
      // For square grids, calculate from all points
      const gridType = this.businessInfo.grid_shape || this.businessInfo.gridType || 'square';
      
      if (gridType === 'circle' || gridType === 'circular') {
        // Use the scan's center coordinates if available
        if (this.businessInfo.latitude && this.businessInfo.longitude) {
          return {
            lat: parseFloat(this.businessInfo.latitude),
            lng: parseFloat(this.businessInfo.longitude)
          };
        }
      }
      
      // Calculate center from all grid points
      let sumLat = 0;
      let sumLng = 0;
      let count = 0;
      
      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          sumLat += parseFloat(item.latitude);
          sumLng += parseFloat(item.longitude);
          count++;
        }
      });
      
      if (count > 0) {
        return { 
          lat: sumLat / count, 
          lng: sumLng / count 
        };
      }
      
      return { lat: 28.5887354, lng: 77.0829215 };
    },

    drawGrid() {
      // Clear existing grid
      if (this.gridRectangle) {
        this.gridRectangle.setMap(null);
        this.gridRectangle = null;
      }
      if (this.gridCircle) {
        this.gridCircle.setMap(null);
        this.gridCircle = null;
      }

      const center = this.getGridCenter();
      const radius = this.calculateGridRadius();
      
      // Check if grid is circular or square based on scan data
      const gridType = this.businessInfo.grid_shape || this.businessInfo.gridType || 'square';
      
      if (gridType === 'circle' || gridType === 'circular') {
        // Draw circle that fits exactly to the edge markers
        this.gridCircle = new google.maps.Circle({
          center: center,
          radius: radius,
          fillColor: '#2477f6',
          fillOpacity: 0.05,
          strokeColor: '#2477f6',
          strokeOpacity: 0.8,
          strokeWeight: 2.5,
          map: this.map
        });
      } else {
        // Draw rectangle
        const bounds = this.calculateSquareBounds(center, radius);
        
        this.gridRectangle = new google.maps.Rectangle({
          bounds: bounds,
          fillColor: '#2477f6',
          fillOpacity: 0.05,
          strokeColor: '#2477f6',
          strokeOpacity: 0.8,
          strokeWeight: 2.5,
          map: this.map
        });
      }
    },

    calculateGridRadius() {
      // Calculate radius from grid points
      if (this.processItems.length < 2) {
        return 1000; // Default 1km
      }
      
      const center = this.getGridCenter();
      let maxDistance = 0;
      
      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          const distance = this.getDistanceFromLatLonInMeters(
            center.lat, center.lng,
            parseFloat(item.latitude), parseFloat(item.longitude)
          );
          if (distance > maxDistance) {
            maxDistance = distance;
          }
        }
      });
      
      // For circle, use exact max distance to touch the edge markers
      return maxDistance;
    },

    calculateSquareBounds(center, radiusInMeters) {
      // Get actual bounds from the grid points to make rectangle fit exactly
      if (this.processItems.length > 0) {
        let minLat = Infinity, maxLat = -Infinity;
        let minLng = Infinity, maxLng = -Infinity;
        
        this.processItems.forEach(item => {
          if (item.latitude && item.longitude) {
            const lat = parseFloat(item.latitude);
            const lng = parseFloat(item.longitude);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          }
        });
        
        // Add small padding to ensure markers are on the border
        const latPadding = (maxLat - minLat) * 0;
        const lngPadding = (maxLng - minLng) * 0;
        
        return {
          north: maxLat + latPadding,
          south: minLat - latPadding,
          east: maxLng + lngPadding,
          west: minLng - lngPadding
        };
      }
      
      // Fallback to calculated bounds
      const latChange = (radiusInMeters / 111320);
      const lngChange = (radiusInMeters / (111320 * Math.cos(center.lat * Math.PI / 180)));
      
      return {
        north: center.lat + latChange,
        south: center.lat - latChange,
        east: center.lng + lngChange,
        west: center.lng - lngChange
      };
    },

    getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
      const R = 6371000; // Radius of the earth in meters
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const d = R * c;
      return d;
    },

    deg2rad(deg) {
      return deg * (Math.PI/180);
    },

    createMarkers() {
      this.clearMarkers();

      this.processItems.forEach((item, index) => {
        if (item.latitude && item.longitude) {
          const position = new google.maps.LatLng(parseFloat(item.latitude), parseFloat(item.longitude));
          const marker = this.createMarker(position, item, index);
          this.markers.push(marker);
        }
      });
    },

    createMarker(position, item, index) {
      const isCompleted = item.status === 'completed';
      let displayText = '?';
      if (isCompleted) {
        if (item.rank === 0) {
          displayText = '20+';
        } else if (item.rank) {
          displayText = item.rank.toString();
        } else {
          displayText = 'NR';
        }
      }
      const color = isCompleted ? this.getRankColor(item.rank) : '#dcdbdb'; // Gray for loading

      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(color, displayText))}`,
          scaledSize: new google.maps.Size(32, 48),
          anchor: new google.maps.Point(16, 48)
        },
        title: `${item.keyword || 'Unknown'} - ${isCompleted ? `Rank ${item.rank || 'NR'}` : 'Pending'}`
      });
      
      // Add click listener to show popup
      marker.addListener('click', () => {
        this.showMarkerDetails(item, position);
      });

      return marker;
    },

    createMarkerSVG(color, displayText) {
      return `
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M16 0C7.16 0 0 7.16 0 16c0 11.2 14.4 30.4 15.36 31.52c0.16 0.16 0.4 0.32 0.64 0.32s0.48-0.16 0.64-0.32C17.6 46.4 32 27.2 32 16c0-8.84-7.16-16-16-16z" fill="${color}" stroke="#888888" stroke-width="1"/>
            <text x="16" y="21" text-anchor="middle" fill="#FFFFFF" font-size="13" font-weight="600" font-family="Arial" stroke="#333333" stroke-width="2" paint-order="stroke fill">${displayText}</text>
          </g>
        </svg>
      `;
    },

    getRankColor(rank) {
      if (rank === 'NR' || !rank || rank === 0) return '#dcdbdb';
      const numRank = parseInt(rank);
      if (numRank <= 5) return '#3EBB80'; // Green for 1-5
      if (numRank <= 14) return '#f59e0b'; // Orange for 6-14
      if (numRank <= 20) return '#FF0000'; // Red for 15-20
      return '#dcdbdb'; // Gray for 20+
    },

    getRankBadgeClass(rank) {
      if (rank <= 3) return 'rank-high';
      if (rank <= 10) return 'rank-medium';
      return 'rank-low';
    },

    clearMarkers() {
      this.markers.forEach(marker => {
        marker.setMap(null);
      });
      this.markers = [];
    },

    fitMapToGrid() {
      if (!this.map || this.processItems.length === 0) return;

      const bounds = new google.maps.LatLngBounds();
      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          bounds.extend(new google.maps.LatLng(parseFloat(item.latitude), parseFloat(item.longitude)));
        }
      });

      this.map.fitBounds(bounds);
      
      // Add padding and limit zoom
      const listener = google.maps.event.addListener(this.map, "idle", () => {
        if (this.map.getZoom() > 16) this.map.setZoom(16);
        google.maps.event.removeListener(listener);
      });
    },

    async fetchScanData() {
      try {
        // Set status to loading if this is the initial load
        if (!this.processItems || this.processItems.length === 0) {
          this.scanStatus = 'loading';
        }

        const id = this.$route.params.id;
        const res = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/get-grid-points/${id}`
        );

        if (res.data.success) {
          this.businessInfo = res.data.data;

          // Store the Google Maps API key if provided
          if (res.data.googleMapsApiKey) {
            this.googleMapsApiKey = res.data.googleMapsApiKey;
          }

          const firstScanItem = res.data.data.ScanItems && res.data.data.ScanItems[0];

          if (firstScanItem) {
            this.processItems = firstScanItem.ProcessItems || [];

            // Check scan status
            this.checkScanStatus();

            // Calculate stats
            this.calculateStats();

            // Extract unique competitors
            this.extractUniqueCompetitors();

            // Create chart after DOM updates if completed
            // On mobile, only create chart if business details are expanded
            const isMobile = this.$vuetify.breakpoint.smAndDown;
            if (this.scanStatus === 'completed' && (!isMobile || !this.isBusinessDetailsCollapsed)) {
              this.$nextTick(() => {
                this.createChart();
              });
            }

            // Update map if initialized
            if (this.map) {
              this.createMarkers();
              this.drawGrid();
              this.fitMapToGrid();
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch scan data:', err);
      }
    },
    
    checkScanStatus() {
      // Check if all process items are completed
      const allCompleted = this.processItems.length > 0 && 
                          this.processItems.every(item => item.status === 'completed');
      
      if (allCompleted) {
        this.scanStatus = 'completed';
        // Stop polling if it's running
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
      } else {
        this.scanStatus = 'pending';
      }
    },
    
    startPollingIfNeeded() {
      // Only start polling if scan is pending
      if (this.scanStatus === 'pending' && !this.pollingInterval) {
        // Poll every 5 seconds
        this.pollingInterval = setInterval(() => {
          this.fetchScanData();
        }, 5000);
      }
    },

    calculateStats() {
      let high = 0, medium = 0, low = 0, out = 0;
      let totalRank = 0;

      // Get total grid locations from grid_size
      const gridSize = this.businessInfo.grid_size || '3x3';
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3);
      const totalGridLocations = rows * cols;

      this.processItems.forEach(item => {
        // For average calculation, treat rank 0 as rank 20
        const rankForAvg = (!item.rank || item.rank === 0) ? 20 : item.rank;
        totalRank += rankForAvg;

        if (!item.rank || item.rank === 0) {
          out++;
        } else if (item.rank <= 5) {
          high++;
        } else if (item.rank <= 14) {
          medium++;
        } else if (item.rank <= 20) {
          low++;
        } else {
          out++;
        }
      });

      // Add rank 20 for any missing locations (grid locations without process items)
      const missingLocations = totalGridLocations - this.processItems.length;
      if (missingLocations > 0) {
        totalRank += missingLocations * 20;
        out += missingLocations;
      }

      const total = totalGridLocations || 1;
      this.highCount = Math.round((high / total) * 100);
      this.mediumCount = Math.round((medium / total) * 100);
      this.lowCount = Math.round((low / total) * 100);
      this.outCount = Math.round((out / total) * 100);

      // Always divide by total grid locations
      this.avgRank = (totalRank / total).toFixed(2);
    },

    extractUniqueCompetitors() {
      const competitorsMap = new Map();
      // Get main business identifier - could be place_id or google_place_id
      const mainBusinessPlaceId = this.businessInfo.place_id || this.businessInfo.google_place_id;
      const mainBusinessName = this.businessInfo.business_name;

      // Get grid size for average calculation
      const gridSize = this.businessInfo.grid_size || '3x3';
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3);
      const totalGridLocations = rows * cols;

      this.processItems.forEach(item => {
        if (item.result) {
          let results = item.result;
          if (typeof results === 'string') {
            try {
              results = JSON.parse(results);
            } catch (e) {
              return;
            }
          }

          if (Array.isArray(results)) {
            // Process ALL results, not just first 20
            results.forEach((business, index) => {
              const placeId = business.place_id;
              if (placeId && !competitorsMap.has(placeId)) {
                // Check if this is the main business by place_id or name match
                const isMainBusiness = (mainBusinessPlaceId && placeId === mainBusinessPlaceId) ||
                                      (mainBusinessName && business.name &&
                                       business.name.toLowerCase() === mainBusinessName.toLowerCase());

                competitorsMap.set(placeId, {
                  name: business.name || 'Unknown Business',
                  address: business.formatted_address || business.vicinity || 'Address not available',
                  rating: business.rating || null,
                  reviews: business.user_ratings_total || 0,
                  place_id: placeId,
                  rankSum: index + 1,  // Start with first rank
                  rankCount: 1,  // Count appearances
                  bestRank: index + 1,  // Track best rank separately
                  isMainBusiness: isMainBusiness,
                  // Add location coordinates for map markers
                  latitude: business.geometry && business.geometry.location ? business.geometry.location.lat : null,
                  longitude: business.geometry && business.geometry.location ? business.geometry.location.lng : null
                });
              } else if (placeId && competitorsMap.has(placeId)) {
                const existing = competitorsMap.get(placeId);
                // Add to rank sum for average calculation
                existing.rankSum += (index + 1);
                existing.rankCount += 1;
                // Update to best (lowest) rank across all grid points
                if (index + 1 < existing.bestRank) {
                  existing.bestRank = index + 1;
                }
              }
            });
          }
        }
      });

      // Calculate average rank for each competitor
      this.uniqueCompetitors = Array.from(competitorsMap.values())
        .map(competitor => {
          // For businesses not found in some grid points, treat as rank 20
          const missingCount = totalGridLocations - competitor.rankCount;
          const totalRankSum = competitor.rankSum + (missingCount * 20);
          const avgRank = totalRankSum / totalGridLocations;

          return {
            ...competitor,
            avgRank: avgRank.toFixed(2),
            bestRank: competitor.bestRank // Keep the actual best rank
          };
        })
        .sort((a, b) => parseFloat(a.avgRank) - parseFloat(b.avgRank));
    },

    showMarkerDetails(item, position) {
      let businesses = [];
      if (item.result) {
        let results = item.result;
        if (typeof results === 'string') {
          try {
            results = JSON.parse(results);
          } catch (e) {
            results = [];
          }
        }
        businesses = Array.isArray(results) ? results.slice(0, 20) : [];
      }

      // Add average rank to each business in the popup
      const businessesWithAvgRank = businesses.map((business, index) => {
        // Find the competitor in uniqueCompetitors array to get average rank
        const competitor = this.uniqueCompetitors.find(c => c.place_id === business.place_id);
        return {
          ...business,
          localRank: index + 1, // Rank at this specific location
          avgRank: competitor ? competitor.avgRank : null // Average rank across all locations
        };
      });

      this.selectedMarkerData = {
        coordinates: `${position.lat().toFixed(6)}, ${position.lng().toFixed(6)}`,
        businesses: businessesWithAvgRank
      };
      this.showMarkerPopup = true;
    },
    
    closeMarkerPopup() {
      this.showMarkerPopup = false;
      this.selectedMarkerData = null;
    },
    
    showCompetitorMarker(competitor) {
      // Only show marker if competitor has valid coordinates
      if (!competitor.latitude || !competitor.longitude) {
       
        return;
      }

      if (!this.map) {
      
        return;
      }

      // Remove existing hover marker if any
      this.hideCompetitorMarker();
      
      const position = new google.maps.LatLng(
        parseFloat(competitor.latitude),
        parseFloat(competitor.longitude)
      );
      console.log(position)
      // Create a custom marker for the competitor
      this.hoverMarker = new google.maps.Marker({
        position: position,
        map: this.map,
        icon: {
          url: this.createCompetitorMarkerSVG(competitor),
          scaledSize: new google.maps.Size(32, 48),
          anchor: new google.maps.Point(16, 48)
        },
        title: `${competitor.name} - Avg Rank #${competitor.avgRank}`,
        zIndex: 9999 // Ensure it appears on top
      });
      
      // Optional: Center map on competitor (you can remove this if you don't want the map to move)
      // this.map.panTo(position);
    },
    
    hideCompetitorMarker() {
      if (this.hoverMarker) {
        this.hoverMarker.setMap(null);
        this.hoverMarker = null;
      }
    },

    switchToCompetitor(competitor) {
      // Store original business info if not already stored
      if (!this.originalBusinessInfo) {
        this.originalBusinessInfo = { ...this.businessInfo };
      }

      // Set the viewing business
      this.viewingBusiness = competitor;

      // Update business info to show competitor's data
      this.businessInfo = {
        business_name: competitor.name,
        address: competitor.address,
        place_id: competitor.place_id,
        google_place_id: competitor.place_id,
        rating: competitor.rating,
        reviews: competitor.reviews,
        // Keep original scan data
        createdAt: this.businessInfo.createdAt,
        keywords: this.businessInfo.keywords,
        grid_size: this.businessInfo.grid_size,
        radius: this.businessInfo.radius,
        scan_radius: this.businessInfo.scan_radius,
        radius_unit: this.businessInfo.radius_unit
      };

      // Recalculate stats and markers for this business
      this.recalculateForBusiness(competitor);

      // Close competitors list after selection (both desktop and mobile)
      this.showCompetitors = false;

      // Hide hover marker after selection
      this.hideCompetitorMarker();
    },

    switchBackToMainBusiness() {
      // Restore original business info
      if (this.originalBusinessInfo) {
        this.businessInfo = { ...this.originalBusinessInfo };
      }

      // Clear viewing business
      this.viewingBusiness = null;

      // Recalculate stats and markers for main business
      this.recalculateForBusiness(null);
    },

    recalculateForBusiness(business) {
      // Recalculate ranks for the selected business
      const targetPlaceId = business ? business.place_id :
                           (this.originalBusinessInfo ?
                            (this.originalBusinessInfo.place_id || this.originalBusinessInfo.google_place_id) :
                            (this.businessInfo.place_id || this.businessInfo.google_place_id));

      const targetName = business ? business.name :
                        (this.originalBusinessInfo ? this.originalBusinessInfo.business_name :
                         this.businessInfo.business_name);

      // Update process items with new ranks
      this.processItems.forEach(item => {
        if (item.result) {
          let results = item.result;
          if (typeof results === 'string') {
            try {
              results = JSON.parse(results);
            } catch (e) {
              item.rank = 0;
              return;
            }
          }

          if (Array.isArray(results)) {
            // Find the rank of the target business
            const businessIndex = results.findIndex(b => {
              const placeId = b.place_id;
              return (targetPlaceId && placeId === targetPlaceId) ||
                     (targetName && b.name && b.name.toLowerCase() === targetName.toLowerCase());
            });

            item.rank = businessIndex >= 0 ? businessIndex + 1 : 0;
          } else {
            item.rank = 0;
          }
        } else {
          item.rank = 0;
        }
      });

      // Recalculate stats
      this.calculateStats();

      // Update markers on map
      if (this.map) {
        this.createMarkers();
      }

      // Update chart
      if (this.chart) {
        this.createChart();
      }
    },
    
    createCompetitorMarkerSVG(competitor) {
      // Create a distinct marker for competitors (different color/style than grid markers)
      const outerColor = competitor.isMainBusiness ? '#22c55e' : '#EA4335'; // Green for you, Google red for competitors
      const innerColor = competitor.isMainBusiness ? '#1a9e4a' : '#C5221F'; // Darker green for you, darker red for competitors (Google style)

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M16 0C7.16 0 0 7.16 0 16c0 11.2 14.4 30.4 15.36 31.52c0.16 0.16 0.4 0.32 0.64 0.32s0.48-0.16 0.64-0.32C17.6 46.4 32 27.2 32 16c0-8.84-7.16-16-16-16z" fill="${outerColor}" stroke="white" stroke-width="1.5"/>
            <circle cx="16" cy="16" r="7" fill="${innerColor}"/>
          </g>
        </svg>
      `)}`;
    },
    
    viewReport() {
      // Navigate to scan-report page with the current scan ID
      const scanId = this.$route.params.id;
      if (scanId) {
        this.$router.push({ name: 'scan-report', params: { id: scanId } });
      }
    },


    rescanMenu() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      this.rerunScan(scanId);
    },

    async rerunScan(scanId) {
      try {
        this.$swal({
          title: 'Rescanning...',
          text: 'Please wait while we initiate a new scan',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/rerun/${scanId}`,
          {}
        );

        if (response.data.success) {
          this.$swal({
            icon: 'success',
            title: 'Scan Initiated',
            text: 'The scan has been queued and will start processing shortly.',
            confirmButtonText: 'View New Scan',
            showCancelButton: true,
            cancelButtonText: 'Stay Here'
          }).then((result) => {
            if (result.isConfirmed) {
              // Navigate to the new scan
              this.$router.push({ name: 'view-grid', params: { id: response.data.data.scanId } });
            } else {
              // Reload current scan data to show updated status
              this.fetchScanData();
            }
          });
        }
      } catch (error) {
        console.error('Error rerunning scan:', error);
        this.$swal({
          icon: 'error',
          title: 'Rescan Failed',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to initiate rescan. Please try again.'
        });
      }
    },

    shareScan() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      if (scanId) {
        this.shareScanId = scanId;
        this.shareScanType = 'quick_scan';
        this.showShareModal = true;
      }
    },

    closeShareModal() {
      this.showShareModal = false;
      this.shareScanId = null;
      this.shareScanType = null;
    },

    onShareCopied() {
      this.$swal({
        icon: 'success',
        title: 'Copied!',
        text: 'Link copied to clipboard',
        timer: 2000,
        showConfirmButton: false
      });
    },

    async exportPNG() {
      this.showReportMenu = false;

      let mapContainer = null;
      try {
        this.$swal({
          title: 'Generating PNG...',
          text: 'Please wait while we create your image',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Create a hidden container for the map
        mapContainer = document.createElement('div');
        mapContainer.style.position = 'fixed';
        mapContainer.style.left = '0';
        mapContainer.style.top = '0';
        mapContainer.style.width = '1200px';
        mapContainer.style.height = '800px';
        mapContainer.style.backgroundColor = 'white';
        mapContainer.style.zIndex = '-9999';
        mapContainer.style.visibility = 'hidden';
        document.body.appendChild(mapContainer);

        // Create header section
        const headerSection = document.createElement('div');
        headerSection.style.padding = '20px';
        headerSection.style.backgroundColor = '#f8f9fa';
        headerSection.style.borderBottom = '1px solid #dee2e6';

        const businessName = this.viewingBusiness ?
          this.viewingBusiness.name :
          (this.businessInfo.business_name || this.businessInfo.name || 'Business');

        const address = this.viewingBusiness ?
          this.viewingBusiness.address :
          (this.businessInfo.address || '');

        headerSection.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h2 style="margin: 0; color: #212529; font-size: 24px;">${businessName}</h2>
              <p style="margin: 5px 0 0 0; color: #6c757d; font-size: 14px;">${address}</p>
            </div>
            <div style="text-align: right;">
              <div style="color: #6c757d; font-size: 12px;">Scan Type: Grid Scan</div>
              <div style="color: #6c757d; font-size: 12px;">Date: ${new Date().toLocaleDateString()}</div>
            </div>
          </div>
        `;
        mapContainer.appendChild(headerSection);

        // Create map div
        const mapDiv = document.createElement('div');
        mapDiv.style.width = '100%';
        mapDiv.style.height = '700px';
        mapContainer.appendChild(mapDiv);

        // Initialize Google Maps - use API key from backend response, store, or env
        const apiKey = this.googleMapsApiKey ||
                      this.$store.getters['apiKeys/googleMapsKey'] ||
                      process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

        if (!apiKey) {
          if (mapContainer) document.body.removeChild(mapContainer);
          throw new Error('Google Maps API key not configured');
        }

        // Make container visible for map initialization
        mapContainer.style.visibility = 'visible';
        mapContainer.style.zIndex = '-1';

        // Create new map instance without controls for clean PNG export
        const map = new google.maps.Map(mapDiv, {
          zoom: this.map.getZoom(),
          center: this.map.getCenter(),
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });

        // Trigger resize to ensure map renders
        google.maps.event.trigger(map, 'resize');

        // Draw grid overlay
        const gridType = this.businessInfo.grid_shape || this.businessInfo.gridType || 'square';

        if (this.processItems.length > 0) {
          const center = this.getGridCenter();

          if (gridType === 'circle' || gridType === 'circular') {
            // Calculate radius
            let maxDistance = 0;
            this.processItems.forEach(item => {
              if (item.latitude && item.longitude) {
                const distance = this.getDistanceFromLatLonInMeters(
                  center.lat, center.lng,
                  parseFloat(item.latitude), parseFloat(item.longitude)
                );
                maxDistance = Math.max(maxDistance, distance);
              }
            });

            const radius = maxDistance * 1.1;

            new google.maps.Circle({
              center: center,
              radius: radius,
              fillColor: '#2477f6',
              fillOpacity: 0.05,
              strokeColor: '#2477f6',
              strokeOpacity: 0.8,
              strokeWeight: 2.5,
              map: map
            });
          } else {
            // Calculate square bounds
            let minLat = Infinity, maxLat = -Infinity;
            let minLng = Infinity, maxLng = -Infinity;

            this.processItems.forEach(item => {
              if (item.latitude && item.longitude) {
                const lat = parseFloat(item.latitude);
                const lng = parseFloat(item.longitude);
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
              }
            });

            const latPadding = (maxLat - minLat) * 0;
            const lngPadding = (maxLng - minLng) * 0;

            new google.maps.Rectangle({
              bounds: {
                north: maxLat + latPadding,
                south: minLat - latPadding,
                east: maxLng + lngPadding,
                west: minLng - lngPadding
              },
              fillColor: '#2477f6',
              fillOpacity: 0.05,
              strokeColor: '#2477f6',
              strokeOpacity: 0.8,
              strokeWeight: 2.5,
              map: map
            });
          }
        }

        // Add markers from current data
        const bounds = new google.maps.LatLngBounds();
        let hasMarkers = false;

        // Add markers for all process items
        if (this.processItems && this.processItems.length > 0) {
          this.processItems.forEach(item => {
            if (item.latitude && item.longitude) {
              const position = new google.maps.LatLng(
                parseFloat(item.latitude),
                parseFloat(item.longitude)
              );

              const rank = item.rank || 0;
              const markerColor = this.getMarkerColor(rank);
              const displayText = rank === 0 || rank > 20 ? '20+' : rank.toString();

              new google.maps.Marker({
                position: position,
                map: map,
                icon: {
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(markerColor, displayText))}`,
                  scaledSize: new google.maps.Size(32, 48),
                  anchor: new google.maps.Point(16, 48)
                }
              });

              bounds.extend(position);
              hasMarkers = true;
            }
          });
        }

        // Fit bounds if we have markers
        if (hasMarkers) {
          map.fitBounds(bounds);
        }

        // Wait for map to load
        await new Promise(resolve => {
          google.maps.event.addListenerOnce(map, 'idle', resolve);
        });

        // Additional wait for tiles to load
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Import html2canvas dynamically
        const html2canvas = (await import('html2canvas')).default;

        // Hide container from view but keep it rendered
        mapContainer.style.position = 'absolute';
        mapContainer.style.left = '-9999px';

        // Capture the container
        const canvas = await html2canvas(mapContainer, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          logging: false,
          backgroundColor: '#ffffff'
        });

        // Remove temporary container
        document.body.removeChild(mapContainer);

        // Convert to blob and download
        canvas.toBlob((blob) => {
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.href = url;

          const cleanBusinessName = businessName.replace(/[^a-z0-9]/gi, '_');
          const dateStr = new Date().toISOString().split('T')[0];

          link.download = `grid_scan_${cleanBusinessName}_${dateStr}.png`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.$swal({
            icon: 'success',
            title: 'Export Successful',
            text: 'Your PNG has been downloaded',
            timer: 2000,
            showConfirmButton: false
          });
        }, 'image/png');

      } catch (error) {
        console.error('Error exporting PNG:', error);
        this.$swal.close(); // Close the loading dialog first

        // Clean up if error
        if (mapContainer && mapContainer.parentNode) {
          document.body.removeChild(mapContainer);
        }

        setTimeout(() => {
          this.$swal({
            icon: 'error',
            title: 'Export Failed',
            text: error.message || 'Failed to export PNG. Please try again.'
          });
        }, 100);
      }
    },

    getMarkerColor(rank) {
      if (!rank || rank === 0 || rank > 20) return '#dcdbdb';
      if (rank <= 5) return '#3EBB80'; // Green for 1-5
      if (rank <= 14) return '#f59e0b'; // Orange for 6-14
      if (rank <= 20) return '#FF0000'; // Red for 15-20
      return '#dcdbdb';
    },

    // Using the unified createMarkerSVG method above

    async exportCSV() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;

      try {
        // Show loading
        this.$swal({
          title: 'Exporting CSV...',
          text: 'Please wait while we prepare your export',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Use the quick scan export endpoint
        const exportEndpoint = `${process.env.VUE_APP_API_ENDPOINT}/api/scans/export-csv/${scanId}`;

        // Fetch CSV data
        const response = await axios.get(
          exportEndpoint,
          {
            responseType: 'blob' // Important for file download
          }
        );

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);

        // Get business name from businessInfo
        const businessName = this.businessInfo.business_name ?
          this.businessInfo.business_name.replace(/[^a-z0-9]/gi, '_') : 'scan';
        const dateStr = new Date().toISOString().split('T')[0];

        link.setAttribute('download', `quick_scan_${businessName}_${dateStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        URL.revokeObjectURL(url);

        // Show success
        this.$swal({
          icon: 'success',
          title: 'Export Successful',
          text: 'Your CSV file has been downloaded',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error exporting CSV:', error);

        // Check if error response is JSON or text
        let errorMessage = 'Failed to export CSV';
        if (error.response) {
          if (error.response.data instanceof Blob) {
            // If response is a blob, try to read it as text
            try {
              const text = await error.response.data.text();
              const jsonError = JSON.parse(text);
              errorMessage = jsonError.message || errorMessage;
            } catch (e) {
              // If can't parse, use default message
            }
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }

        this.$swal({
          icon: 'error',
          title: 'Export Failed',
          text: errorMessage
        });
      }
    },

    deleteScan() {
      this.showReportMenu = false;
      const scanId = this.$route.params.id;
      this.deleteIds = [scanId];
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        const response = await axios.delete(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete`,
          {
            data: { scanIds: this.deleteIds }
          }
        );

        if (response.data.success) {
          // Close dialog
          this.showDeleteDialog = false;

          // Show success message
          this.$swal({
            icon: 'success',
            title: 'Deleted!',
            text: response.data.message || 'The scan has been deleted successfully.',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            // Redirect to scans page
            this.$router.push({ name: 'scans' });
          });
        }
      } catch (error) {
        console.error('Error deleting scan:', error);

        // Show error message
        this.$swal({
          icon: 'error',
          title: 'Deletion Failed',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to delete the scan. Please try again.'
        });

        // Close dialog on error too
        this.showDeleteDialog = false;
      } finally {
        this.deleting = false;
      }
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.deleteIds = [];
    },

    isMainBusiness(business) {
      const mainBusinessPlaceId = this.businessInfo.place_id || this.businessInfo.google_place_id;
      const mainBusinessName = this.businessInfo.business_name;
      
      return (mainBusinessPlaceId && business.place_id === mainBusinessPlaceId) || 
             (mainBusinessName && business.name && 
              business.name.toLowerCase() === mainBusinessName.toLowerCase());
    },
    
    getRankBadgeColor(rank) {
      if (rank <= 3) return 'rank-top';
      if (rank <= 10) return 'rank-high';
      if (rank <= 20) return 'rank-medium';
      return 'rank-low';
    },
    
    formatRankDisplay(rank) {
      if (rank <= 20) return rank.toFixed(2);
      return '20+';
    },

    toggleMobileBusinessDetails() {
      this.isBusinessDetailsCollapsed = !this.isBusinessDetailsCollapsed;

      // If expanding and on mobile, ensure chart is created
      if (!this.isBusinessDetailsCollapsed && this.$vuetify.breakpoint.smAndDown && this.scanStatus === 'completed') {
        this.$nextTick(() => {
          setTimeout(() => {
            try {
              // Force chart recreation for mobile
              if (this.chart) {
                this.chart.destroy();
                this.chart = null;
              }
            } catch (e) {
              // If destroy fails, just set to null
              this.chart = null;
            }
            this.createChart();
          }, 150);
        });
      }
    },

    createChart() {
      // Check for both desktop and mobile chart canvas
      // Use mobile canvas on mobile devices, desktop canvas on desktop
      const isMobile = this.$vuetify.breakpoint.smAndDown;
      const canvasRef = isMobile ? this.$refs.mobileChartCanvas : this.$refs.chartCanvas;

      if (!canvasRef) {
        
        return;
      }

      try {
        const ctx = canvasRef.getContext('2d');

        // Safely destroy existing chart
        if (this.chart) {
          try {
            this.chart.destroy();
          } catch (e) {
           
          }
          this.chart = null;
        }

        // Ensure canvas has dimensions
        if (isMobile) {
          canvasRef.width = 60;
          canvasRef.height = 60;
        }

        // Clear the canvas context before creating new chart
        ctx.clearRect(0, 0, canvasRef.width, canvasRef.height);

        this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['High', 'Medium', 'Low', 'Out'],
          datasets: [{
            data: [this.highCount, this.mediumCount, this.lowCount, this.outCount],
            backgroundColor: ['#3EBB80', '#f59e0b', '#FF0000', '#dcdbdb'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                return label + ': ' + value + '%';
              }
            }
          },
          cutoutPercentage: 65
        }
      });
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    }
  },

  beforeDestroy() {
    // Clear polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    this.clearMarkers();
    if (this.gridRectangle) {
      this.gridRectangle.setMap(null);
    }
    if (this.gridCircle) {
      this.gridCircle.setMap(null);
    }
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (e) {
        // Ignore destroy errors on cleanup
      }
      this.chart = null;
    }
  }
}
</script>

<style scoped>
/* Breadcrumb strip styles */
.breadcrumb-strip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 16px;
  z-index: 5;  /* Below sidebar overlay but above map */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.breadcrumb-item {
  font-size: 12px;
  background: linear-gradient(90deg, #6b7280 0%, #9ca3af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
}

.breadcrumb-item.clickable {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  transition: all 0.2s;
}

.breadcrumb-item.clickable:hover {
  background: linear-gradient(90deg, #374151 0%, #4b5563 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: underline;
}

.breadcrumb-item.current {
  background: linear-gradient(90deg, #374151 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 6px;
  color: #9ca3af;
  font-size: 14px;
}

.view-grid-container {
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.view-grid-container.with-breadcrumb {
  height: calc(100vh - 30px);
  margin-top: 30px;
}

/* Embed container styles */
.embed-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  margin: 0;
  padding: 0;
}

.embed-container .view-grid-container {
  height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0;
}

/* Full Page Map */
.google-map-full {
  width: 100%;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
}

/* Left Sidebar Overlay */
.sidebar-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  width: 420px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 10;
  transition: top 0.3s ease;
}

/* Adjust sidebar for embed mode */
.embed-container .sidebar-overlay {
  max-height: calc(100vh - 60px);
}

/* Adjust sidebar position when breadcrumb is shown in embedded mode */
.view-grid-container.with-breadcrumb .sidebar-overlay {
  top: 50px;  /* 20px original + 30px breadcrumb */
  max-height: calc(100vh - 70px);  /* Adjust height accordingly */
}


/* Custom Scrollbar */
.sidebar-overlay::-webkit-scrollbar {
  width: 6px;
}

.sidebar-overlay::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-overlay::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sidebar-overlay::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Business Card Block */
.business-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Business Section */
.business-section {
  display: flex;
  gap: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.business-info {
  flex: 1;
}

.business-name {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

/* Average Section */
.average-section {
  padding: 12px 16px;
  background: #3EBB8026;
  border-radius: 6px;
  text-align: center;
  min-width: 80px;
  align-self: flex-start;
}

.average-section .section-label {
  font-size: 8px;
  color: #00000080;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-weight: 600;
}

.average-value {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4b5563;
  line-height: 1.5;
}

.info-row i {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
  width: 14px;
  flex-shrink: 0;
}

/* Keyword Section */
.keyword-section {
  margin-top: 8px;
  padding: 0px 16px 8px 16px;
  /* background: #f3f4f6; */
  border-radius: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.section-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  font-weight: 600;
}

.keyword-value {
  font-size: 13px;
  color: #111827;
  font-weight: 500;
  line-height: 1.4;
}

/* Chart Section - Inside Business Card */
.chart-section-inner {
  padding: 16px;
  background: white;
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-section-inner .chart-container {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
}

.chart-section-inner .stats-row {
  display: flex;
  flex: 1;
  gap: 16px;
}

/* Old Chart Section - Kept for reference but not used */
.chart-section {
  background: white;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
}

.chart-container {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
}

.chart-container canvas {
  width: 100% !important;
  height: 100% !important;
}

.stats-row {
  display: flex;
  flex: 1;
  gap: 16px;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 9px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Competitors Section */
.competitors-section {
  position: fixed;
  bottom: 0px;
  left: 80px; /* Positioned at the left edge after sidebar */
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 420px;
  transition: all 0.3s ease;
}

/* Adjust competitors section for embed mode */
.embed-container .competitors-section {
  left: 20px; /* Align with sidebar in embed mode */
  bottom: 0px;
}

/* Adjust position for public views without sidebar */
.competitors-section.public-view {
  left: 20px;
}

.competitors-header {
  padding: 14px 20px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
  border-radius: 12px 12px 0 0;
}

.competitors-header:hover {
  background: #f9fafb;
}

.competitors-list {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 0;
  animation: slideUp 0.3s ease;
  overflow-y: auto;
  background: white;
  border-radius: 0 0 12px 12px;
}

.competitors-list::-webkit-scrollbar {
  width: 6px;
}

.competitors-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.competitors-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.competitors-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.competitors-section.expanded {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.competitor-card {
  padding: 12px 16px;
  margin: 0 -16px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.competitor-card:last-child {
  border-bottom: none;
}

.competitor-card:hover {
  background: #f9fafb;
}

.competitor-card.active-business {
  background: #fef3c7 !important;
  border-bottom-color: #f59e0b !important;
}

/* Back to Business Button - Floating */
.back-to-business-btn {
  position: absolute;
  top: 20px;
  left: 450px; /* Position to the right of sidebar */
  background: white;
  color: #19233A;
  border: none;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 11;
}

.back-to-business-btn:hover {
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.competitor-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
}

.competitor-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  line-height: 1.3;
}

.you-label {
  color: #3b82f6;
  font-weight: 500;
  margin-left: 4px;
  font-size: 12px;
}

.competitor-rank {
  display: inline-block;
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 15px;
  font-weight: 600;
  flex-shrink: 0;
  margin-left: 8px;
  white-space: nowrap;
}

.rank-high {
  background: #d1fae5;
  color: #065f46;
}

.rank-medium {
  background: #fed7aa;
  color: #92400e;
}

.rank-low {
  background: #fee2e2;
  color: #991b1b;
}

.competitor-address {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
  line-height: 1.4;
}

.competitor-rating {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.stars {
  display: flex;
  gap: 1px;
}

.stars i {
  font-size: 11px;
  color: #e5e7eb;
}

.stars i.filled {
  color: #fbbf24;
}

.rating-value {
  font-weight: 600;
  color: #1f2937;
}

.review-count {
  color: #6b7280;
}

/* Popup Overlay */
.popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 19;
}

/* Mobile drag handle - hidden on desktop */
.mobile-drag-handle {
  display: none;
}

/* Marker Popup */
.marker-popup {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 500px;
  max-height: calc(100vh - 40px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  z-index: 20;
  display: flex;
  flex-direction: column;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.popup-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #111827;
}

.popup-coordinates {
  padding: 8px 20px;
  font-size: 12px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.popup-businesses {
  overflow-y: auto;
  max-height: calc(100vh - 160px);
  padding: 12px;
}

.business-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}

.business-item:hover {
  background: #f9fafb;
}

.business-item:last-child {
  border-bottom: none;
}

.business-rank-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.business-rank-badge.rank-top {
  background: #dcfce7;
  color: #15803d;
}

.business-rank-badge.rank-high {
  background: #dbeafe;
  color: #1e40af;
}

.business-rank-badge.rank-medium {
  background: #fed7aa;
  color: #c2410c;
}

.business-rank-badge.rank-low {
  background: #fee2e2;
  color: #b91c1c;
}

.business-details {
  flex: 1;
  min-width: 0;
}

.business-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.business-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.3;
}

.you-badge {
  display: inline-block;
  background: #3b82f6;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  font-weight: 500;
}

.rank-display {
  font-size: 13px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 15px;
}

.rank-display.rank-top {
  color: #15803d;
  background: #dcfce7;
}

.rank-display.rank-high {
  color: #1e40af;
  background: #dbeafe;
}

.rank-display.rank-medium {
  color: #c2410c;
  background: #fed7aa;
}

.rank-display.rank-low {
  color: #b91c1c;
  background: #fee2e2;
}

.business-address {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
  line-height: 1.4;
}

.business-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rating-section {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.rating {
  font-weight: 600;
  color: #111827;
}

.stars {
  display: flex;
  gap: 1px;
}

.stars i {
  font-size: 10px;
  color: #e5e7eb;
}

.stars i.filled {
  color: #fbbf24;
}

.reviews {
  color: #6b7280;
}

.scan-btn {
  background: white;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.scan-btn:hover {
  background: #3b82f6;
  color: white;
}

/* Scrollbar for popup businesses */
.popup-businesses::-webkit-scrollbar {
  width: 6px;
}

.popup-businesses::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.popup-businesses::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.popup-businesses::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* VIEW IN GRID Button */
/* Report Button Container */
.report-button-container {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
      padding: 5px;
    background: white;
    border-radius: 5px;
}

/* Adjust report button position when breadcrumb is shown in embedded mode */
.view-grid-container.with-breadcrumb .report-button-container {
  top: 50px;  /* 20px original + 30px breadcrumb height */
}

/* Adjust scan status overlay position when breadcrumb is shown in embedded mode */
.view-grid-container.with-breadcrumb .scan-status-overlay {
  top: 50px;  /* 20px original + 30px breadcrumb height */
}

.view-report-btn {
  background: #2477f6;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.view-report-btn:hover:not(:disabled) {
  background: #1d5fd4;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(36, 119, 246, 0.3);
}

.view-report-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  opacity: 0.7;
}

.view-report-btn:disabled i.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.menu-dots-btn {
  background: white;
  border: 1px solid #e5e7eb;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.menu-dots-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.menu-dots-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

.menu-dots-btn i {
  color: #374151;
}

/* Report Dropdown Menu */
.report-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 100;
  overflow: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s;
  color: #000000;
  font-size: 13px;
  font-weight: 500;
}

.menu-item:hover {
  background: #f3f4f6;
}

.menu-item.delete {
  color: #ef4444;
  border-top: 1px solid #e5e7eb;
}

.menu-item.delete:hover {
  background: #fef2f2;
}

.menu-item i {
  width: 16px;
  font-size: 14px;
  opacity: 0.7;
  margin-right: 2px;
}

/* Scan Status Overlay */
.scan-status-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 15;
}

.status-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  text-align: center;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.spinner {
  animation: rotate 2s linear infinite;
}

.spinner .path {
  stroke: #3b82f6;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 100;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 60, 100;
    stroke-dashoffset: -30;
  }
  100% {
    stroke-dasharray: 60, 100;
    stroke-dashoffset: -90;
  }
}

.status-card h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.status-card p {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
}

.progress-info {
  font-size: 13px;
  font-weight: 500;
  color: #3b82f6;
  padding: 8px 16px;
  background: #eff6ff;
  border-radius: 6px;
  display: inline-block;
}

/* Desktop/Mobile specific classes */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

/* Business Header with Toggle */
.business-header-with-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.business-header-with-toggle .business-name {
  flex: 1;
  margin: 0;
}

/* Mobile Toggle Button */
.mobile-toggle-btn {
  padding: 6px 8px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
  font-size: 16px;
  margin-left: 8px;
}

.mobile-toggle-btn:hover {
  opacity: 0.7;
  background: #f3f4f6;
  border-radius: 4px;
}

/* Mobile Menu Dots */
.mobile-menu-dots {
  padding: 6px 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
  margin-left: 8px;
}

.mobile-menu-dots:hover:not(:disabled) {
  opacity: 0.7;
  background: #f3f4f6;
  border-radius: 4px;
}

.mobile-menu-dots:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
  opacity: 0.5;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  /* Hide desktop, show mobile */
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  /* Fix viewport to prevent scrolling */
  body {
    overflow-x: hidden !important;
  }

  .view-grid-container {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
  }

  /* Sidebar positioned at top on mobile */
  .sidebar-overlay {
    position: fixed !important;
    top: 60px !important; /* Below mobile header */
    left: 10px !important;
    right: 10px !important;
    width: calc(100% - 20px) !important;
    height: auto !important;
    z-index: 100 !important;
    background: transparent;
  }

  /* Status card positioned below mobile header */
  .scan-status-overlay {
    position: fixed !important;
    top: 70px !important; /* Below mobile header */
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: calc(100% - 40px) !important;
    max-width: 340px !important;
    z-index: 101 !important;
  }

  .status-card {
    width: 100% !important;
    min-width: unset !important;
    padding: 20px !important;
  }

  .status-card h3 {
    font-size: 16px !important;
  }

  .status-card p {
    font-size: 13px !important;
  }

  /* Business card adjustments for mobile */
  .business-card {
    width: 100% !important;
    height: auto !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden !important; /* Hide overflow to ensure content fits */
    background: white;
    margin-top: 10px !important; /* Small top margin on mobile */
  }

  /* Business section takes full width with NO padding - override desktop flex */
  .business-section {
    display: block !important; /* Override desktop flex */
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    gap: 0 !important; /* Remove desktop gap */
    border-bottom: none !important; /* Remove desktop border */
  }

  /* Business header takes full width edge to edge */
  .business-header-with-toggle {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    /* padding: 12px 16px !important; */
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .business-header-with-toggle .business-name {
    font-size: 16px !important;
    flex: 1 !important;
  }

  /* Mobile actions container */
  .mobile-actions {
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
  }

  /* Mobile business content */
  .mobile-business-content {
    width: 100% !important;
    animation: slideDown 0.3s ease;
  }

  .mobile-business-content .business-info {
    padding: 12px 16px !important;
  }

  /* Mobile average section - full width edge-to-edge with background */
  .mobile-average-section {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 16px !important;
    background: #3EBB8026 !important;
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    border-radius: 6px

  }

  .mobile-average-section .section-label {
    font-size: 10px !important;
    color: #6b7280 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 0 !important;
    font-weight: 600 !important;
  }

  .mobile-average-section .average-value {
    font-size: 28px !important;
    font-weight: 700 !important;
    color: #111827 !important;
  }

  /* Hide desktop view report button on mobile */
  .report-button-container {
    display: none !important;
  }

  /* Hide floating back to business button on mobile */
  .back-to-business-btn {
    display: none !important;
  }

  /* Mobile Back to Business Button - inside card */
  .mobile-back-button {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    width: 100% !important;
    padding: 12px 16px !important;
    background: #EFF6FF !important;
    border: none !important;
    border-bottom: 1px solid #DBEAFE !important;
    color: #2477F6 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: background 0.2s !important;
    text-align: left !important;
  }

  .mobile-back-button:hover {
    background: #DBEAFE !important;
  }

  .mobile-back-button i {
    font-size: 14px !important;
  }

  /* Competitors section on mobile - keep at bottom */
  .competitors-section {
    position: fixed !important;
    bottom: 0px !important;
    left: 0px !important;
    right: 0px !important;
    width: 100%  !important;
  }

  /* Business info adjustments when expanded */
  .business-info {
    padding-top: 12px;
    animation: slideDown 0.3s ease;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 500px;
    }
  }

  /* Mobile Keyword Section */
  .mobile-keyword-section {
    padding: 16px !important;
    margin: 0 !important;
    background: white !important;
    border-top: 1px solid #e5e7eb !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  /* Mobile Chart Section - horizontal layout */
  .mobile-chart-section {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 16px !important;
    padding: 16px !important;
    background: white !important;
    border-top: 1px solid #e5e7eb !important;
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .mobile-chart-section .chart-container {
    width: 60px !important;
    height: 60px !important;
    flex-shrink: 0 !important;
  }

  .mobile-chart-section .chart-container canvas {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
  }

  .mobile-chart-section .stats-row {
    display: flex !important;
    flex: 1 !important;
    gap: 12px !important;
  }

  .mobile-chart-section .stat-item {
    flex: 1 !important;
    text-align: center !important;
  }

  .mobile-chart-section .stat-value {
    font-size: 14px !important;
    font-weight: 700 !important;
    margin-bottom: 2px !important;
  }

  .mobile-chart-section .stat-label {
    font-size: 9px !important;
    color: #6b7280 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px !important;
  }

  /* Hide desktop chart section on mobile */
  .chart-section-inner {
    display: none !important;
  }

  .chart-section-inner .chart-container {
    width: 60px !important;
    height: 60px !important;
    flex-shrink: 0 !important;
  }

  .chart-section-inner .stats-row {
    display: flex !important;
    flex: 1 !important;
    gap: 12px !important;
  }

  .chart-section-inner .stat-item {
    flex: 1 !important;
    text-align: center !important;
  }

  .chart-section-inner .stat-value {
    font-size: 14px !important;
    font-weight: 700 !important;
    margin-bottom: 2px !important;
  }

  .chart-section-inner .stat-label {
    font-size: 9px !important;
    color: #6b7280 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px !important;
  }

  /* Marker popup responsive - slide up from bottom */
  .marker-popup {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    max-height: 70vh !important; /* Reduced height */
    animation: slideUpFromBottom 0.3s ease !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2) !important;
    z-index: 100;
  }

  /* Mobile drag handle - visible on mobile */
  .mobile-drag-handle {
    display: flex !important;
    justify-content: center !important;
    padding: 8px 0 4px 0 !important;
    cursor: grab !important;
  }

  .mobile-drag-handle span {
    width: 40px !important;
    height: 4px !important;
    background: #d1d5db !important;
    border-radius: 2px !important;
  }

  /* Adjust popup header for mobile */
  .popup-header {
    padding: 12px 16px !important;
    border-bottom: 1px solid #e5e7eb !important;
  }

  .popup-header h3 {
    font-size: 16px !important;
  }

  /* Adjust popup content for mobile */
  .popup-businesses {
    padding: 12px !important;
  }

  .popup-business-card {
    padding: 10px !important;
    margin-bottom: 10px !important;
  }

  @keyframes slideUpFromBottom {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  /* Adjust info rows for mobile */
  .info-row {
    font-size: 12px;
    padding: 4px 0;
  }

  /* Hide desktop average section on mobile */
  .average-section.desktop-only {
    display: none !important;
  }

  /* Desktop average section should not affect mobile layout */
  .average-section {
    display: none !important;
  }

  /* Hide desktop keyword section on mobile */
  .keyword-section {
    display: none !important;
  }

  /* Mobile dropdown menu - positioned relative to three dots */
  .mobile-report-menu {
    position: absolute !important;
    top: 100% !important;
    right: 0 !important;
    margin-top: 4px !important;
    background: white !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
    width: 200px !important;
    z-index: 1200 !important;
    padding: 4px 0 !important;
  }

  .mobile-report-menu .menu-item {
    display: flex !important;
    align-items: center !important;
    padding: 10px 16px !important;
    cursor: pointer !important;
    transition: background 0.2s !important;
    font-size: 14px !important;
    color: #374151 !important;
  }

  .mobile-report-menu .menu-item:hover {
    background: #f3f4f6 !important;
  }

  .mobile-report-menu .menu-item i {
    margin-right: 10px !important;
    font-size: 16px !important;
    color: #6b7280 !important;
  }

  .mobile-report-menu .menu-item.delete {
    color: #ef4444 !important;
  }

  .mobile-report-menu .menu-item.delete i {
    color: #ef4444 !important;
  }

  /* Mobile actions need relative positioning for dropdown */
  .mobile-actions {
    position: relative !important;
  }
}
</style>

<!-- Global styles to remove padding from parent col class -->
<style>
/* Remove padding from parent col class for this view only */
.col {
  padding: 0 !important;
}

/* Also ensure fill-height has no padding */
.fill-height {
  padding: 0 !important;
  margin: 0 !important;
}

/* Remove any container padding as well */
.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
}

/* Remove mobile mode padding */
.mobile-mode .lfp-container {
  padding: 0 !important;
  margin: 0 !important;
}

/* Ensure full viewport on mobile */
@media (max-width: 768px) {
  html, body {
    overflow-x: hidden !important;
    width: 100% !important;
    height: 100% !important;
  }

  .view-grid-container {
    width: 100vw !important;
    height: 100vh !important;
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
  }

  .google-map-full {
    width: 100% !important;
    height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  /* Remove any padding from navigation on mobile */
  .panel-navigation,
  .lfp-container,
  .mobile-mode .lfp-container {
    padding: 0 !important;
  }
}
</style>